"use client";

import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, CreditCard, Headphones, Plane, Quote, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface Review {
  author: string;
  text: string;
  isRtl?: boolean;
}
interface Benefit {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const reviewsData: Review[] = [{
  author: "Alexandra-Sara Loritz",
  text: "The transfer with \"Hurghada Transfer Hub\" was excellent, and I would definitely recommend their shuttle service. The driver was not only attentive and friendly but also punctual.",
  isRtl: false
}, {
  author: "Mandy Jones",
  text: "Used to take 9 of us to the Marina and pick us back up. Efficient, extremely clean mini bus and very friendly staff. Will definitely use again.",
  isRtl: true
}, {
  author: "Familie Van Buren",
  text: "Perfect family transfer with two child seats already installed as requested. Smooth ride to Makadi Bay and very easy communication over WhatsApp.",
  isRtl: false
}];
const benefitsData: Benefit[] = [{
  title: "Fixed Prices",
  description: "All-inclusive rates confirmed beforehand. Never haggle or negotiate at the curb.",
  icon: CreditCard
}, {
  title: "Professional Drivers",
  description: "Courteous, punctual, and licensed drivers who know Egypt's coastal routes thoroughly.",
  icon: BadgeCheck
}, {
  title: "Flight Monitoring",
  description: "We monitor your flight's arrival time and any delays free of charge. Your transfer includes up to 60 minutes of free waiting time from the agreed meeting time.",
  icon: Plane
}, {
  title: "24/7 WhatsApp Support",
  description: "Our team is available 24/7 on WhatsApp to help with itinerary changes, questions, and booking updates.",
  icon: Headphones
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
export default function Testimonials() {
  return <section id="why-travelers-choose-us" data-nav="light" className="relative bg-secondary text-secondary-foreground py-20 lg:py-24 border-y border-border">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Signature Hairline Rule with Turquoise Dot */}
        <div className="relative mb-12 flex items-center justify-center" aria-hidden="true">
          <div className="h-px w-full max-w-xs bg-border relative">
            <span className="absolute -top-[3px] right-0 size-1.5 rounded-full bg-primary" />
          </div>
        </div>

        {/* Section Heading & Rating Line */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-default text-3xl md:text-4xl font-semibold tracking-tight text-black mb-4">
            Why Travelers Choose Us
          </h2>

          {/* Rating Line in Native Visual Form */}
          <div className="inline-flex items-center gap-2.5 rounded-md border border-border bg-card px-4 py-2 shadow-2xs">
            <div className="flex items-center gap-0.5 text-[var(--chart-2)]" aria-label="5 star rating">
              {[0, 1, 2, 3, 4].map(starIndex => <Star key={starIndex} data-index={starIndex} className="size-4 fill-[var(--chart-2)] stroke-[var(--chart-2)]" />)}
            </div>
            <span className="text-sm font-medium text-foreground">Rated 4.9 out of 5 on Google, based on 105 reviews.</span>
          </div>
        </motion.div>

        {/* Testimonials 3-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {reviewsData.map((review, i) => <motion.div key={review.author} variants={itemVariants} data-index={i}>
              <Card className="h-full rounded-md border border-border bg-card text-card-foreground shadow-2xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-sm flex flex-col justify-between p-6 gap-0">
                <CardContent className="p-0 flex flex-col h-full justify-between">
                  <div>
                    <div className="mb-4 inline-flex size-9 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
                      <Quote className="size-4" />
                    </div>
                    <blockquote className="text-sm text-foreground/90 leading-relaxed italic mb-6 font-default !text-left" dir={review.isRtl ? "rtl" : "ltr"}>
                      &ldquo;{review.text}&rdquo;
                    </blockquote>
                  </div>

                  <div className="pt-4 border-t border-border/80 flex items-center justify-between">
                    <span className="font-semibold text-sm text-foreground">
                      {review.author}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <BadgeCheck className="size-3.5 text-primary" />
                      Google review
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* 4 Benefits Horizontal Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-10 border-t border-border">
          {benefitsData.map((benefit, idx) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={idx} className="flex flex-col gap-2 rounded-md bg-card/60 p-5 border border-border shadow-2xs">
                <div className="inline-flex size-10 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary mb-1">
                  <IconComponent className="size-5" />
                </div>
                <h3 className="text-base font-semibold text-black">
                  {benefit.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>;
        })}
        </motion.div>

        {/* Google Reviews CTA Button */}
        <div className="mt-10 pt-8 border-t border-border flex justify-center">
          <Button asChild variant="outline" className="h-auto py-3 px-5 rounded-md border-border bg-card text-foreground shadow-2xs transition-[border-color,background-color,box-shadow] duration-200 hover:border-primary/60 hover:bg-card hover:text-foreground hover:shadow-sm">
            <Link to="https://share.google/9PUBLJfl1Jtt1DJ70" newTab aria-label="View more reviews on Google (opens in a new tab)" className="inline-flex items-center gap-2.5 text-sm font-medium text-foreground">
              <svg className="size-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>View More Reviews on Google</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}